import React from 'react';
import SidebarInput, {
  SidebarInputProps
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/SidebarInput';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import {
  SelectExcludeCheckbox,
  SelectIncludeCheckbox,
  SelectedExcludeCheckbox,
  SelectedIncludeCheckbox
} from 'src/components/SvgIcons';
import UnstyledButton from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/UnstyledFilterButton';
import { Tooltip } from '@mui/material';

export interface GenericSidebarFilterProps {
  title: string;
  includeToggles?: boolean;
  includeInput?: boolean;
  includeClear?: boolean;
  handleClear?: () => void;
  handleToggle?: (toggle: 'include' | 'exclude') => void;
  renderItems?: () => React.ReactNode;
  toggleMode?: 'include' | 'exclude';
  sidebarInputProps?: SidebarInputProps;
}

interface CustomTooltipProps {
  title: string;
  children: React.ReactNode;
}

const FilterTooltip = ({ title, children }: CustomTooltipProps) => (
  <Tooltip
    title={title}
    placement="top"
    sx={{
      width: 52,
      height: 22,
      pt: '2px',
      pr: '6px',
      pb: '2px',
      pl: '6px',
      gap: '10px',
      borderRadius: '4px'
    }}
  >
    {/* Without div, the svg icons will not have hovering tooltip */}
    <div>{children}</div>
  </Tooltip>
);

const SidebarFilter = ({
  title,
  includeToggles,
  includeInput = true,
  includeClear = true,
  handleClear,
  handleToggle,
  renderItems,
  toggleMode = 'include',
  sidebarInputProps
}: GenericSidebarFilterProps) => {
  return (
    <Flex flexDirection="column">
      <Flex flexDirection="column" gap={8}>
        {/* Title */}
        <Flex alignItems="center">
          <Text variant="h6">{title}</Text>
          {includeToggles && (
            <Flex gap={6} margin="0px 10px">
              <UnstyledButton
                onClick={() => {
                  if (handleToggle) {
                    handleToggle('include');
                  }
                }}
              >
                {toggleMode === 'include' ? (
                  <FilterTooltip title="Include">
                    <SelectedIncludeCheckbox style={{ width: '12px', height: '12px' }} />
                  </FilterTooltip>
                ) : (
                  <FilterTooltip title="Include">
                    <SelectIncludeCheckbox style={{ width: '12px', height: '12px' }} />
                  </FilterTooltip>
                )}
              </UnstyledButton>
              <UnstyledButton
                onClick={() => {
                  if (handleToggle) {
                    handleToggle('exclude');
                  }
                }}
              >
                {toggleMode === 'exclude' ? (
                  <FilterTooltip title="Exclude">
                    <SelectedExcludeCheckbox style={{ width: '12px', height: '12px' }} />
                  </FilterTooltip>
                ) : (
                  <FilterTooltip title="Exclude">
                    <SelectExcludeCheckbox style={{ width: '12px', height: '12px' }} />
                  </FilterTooltip>
                )}
              </UnstyledButton>
            </Flex>
          )}

          {includeClear && (
            <UnstyledButton onClick={handleClear} style={{ marginLeft: includeToggles ? '0px' : '8px' }}>
              <Text variant="subtitle3">Clear</Text>
            </UnstyledButton>
          )}
        </Flex>
        {/* Input */}
        {includeInput && <SidebarInput {...sidebarInputProps} />}
      </Flex>

      {renderItems && renderItems()}
    </Flex>
  );
};

export default React.memo(SidebarFilter);
